import { IMAGE_TYPES } from "./types/content-api";
import { canonicalAssetLanguage } from "./vod-title";
import { localisedLanguages } from "./vod-localised";
export { IMAGE_TYPES };
export const ARTWORK_ACCEPT = "image/jpeg,image/png,image/webp,image/avif";
export const IMAGE_TYPE_SPECS = {
    thumbnail: { label: "Thumbnail", shape: "square, 1:1" },
    poster: { label: "Poster", shape: "portrait, 2:3" },
    banner: { label: "Banner", shape: "landscape, 16:9" },
    cover: {
        label: "Cover",
        shape: "landscape, 16:9",
        note: "Nothing upstream distinguishes cover from poster - no validation, no separate consumer. Pick one and be consistent within a catalogue.",
    },
    logo: { label: "Logo", shape: "transparent PNG" },
    other: {
        label: "Unclassified",
        shape: "any",
        note: "The type a newly added image gets in the portal, so a large share of the catalogue carries it. It is not an error - it is an unanswered question.",
    },
};
export const DEFAULT_IMAGE_TYPE = "other";
export const IMAGE_FIELD_LIMITS = {
    url: 2048,
    source: 2048,
    language: 3,
    type: 32,
};
export function imageRows(input) {
    return Array.isArray(input.images) ? [...input.images] : [];
}
export function imageLanguages(input) {
    const seen = new Set();
    for (const row of imageRows(input)) {
        const code = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language);
        if (code)
            seen.add(code);
    }
    return [...seen].sort();
}
export function imagesByLanguage(input) {
    const out = new Map();
    for (const row of imageRows(input)) {
        const code = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language);
        if (!code)
            continue;
        const list = out.get(code);
        if (list)
            list.push(row);
        else
            out.set(code, [row]);
    }
    return out;
}
export function imagesForLanguage(input, language) {
    var _a;
    const code = canonicalAssetLanguage(language);
    if (!code)
        return [];
    return (_a = imagesByLanguage(input).get(code)) !== null && _a !== void 0 ? _a : [];
}
export function imagesOfType(images, type) {
    return images.filter((image) => (image === null || image === void 0 ? void 0 : image.type) === type);
}
export const IMAGE_FALLBACK_LANGUAGE = "en";
export function deliveredImages(input, language) {
    const own = imagesForLanguage(input, language);
    if (own.length > 0)
        return own;
    return imagesForLanguage(input, IMAGE_FALLBACK_LANGUAGE);
}
export function countVodImages(input) {
    var _a, _b, _c, _d;
    const byLanguage = {};
    const byType = {};
    let total = 0;
    for (const row of imageRows(input)) {
        total += 1;
        const code = (_a = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language)) !== null && _a !== void 0 ? _a : String(row === null || row === void 0 ? void 0 : row.language);
        byLanguage[code] = ((_b = byLanguage[code]) !== null && _b !== void 0 ? _b : 0) + 1;
        const type = String((_c = row === null || row === void 0 ? void 0 : row.type) !== null && _c !== void 0 ? _c : "");
        byType[type] = ((_d = byType[type]) !== null && _d !== void 0 ? _d : 0) + 1;
    }
    return { total, byLanguage, byType };
}
export function imageSlotCoverage(input, languages, types) {
    var _a, _b;
    const byLanguage = imagesByLanguage(input);
    let filled = 0;
    for (const language of languages) {
        const code = (_a = canonicalAssetLanguage(language)) !== null && _a !== void 0 ? _a : language;
        const rows = (_b = byLanguage.get(code)) !== null && _b !== void 0 ? _b : [];
        for (const type of types) {
            if (rows.some((row) => row.type === type))
                filled += 1;
        }
    }
    return { filled, total: languages.length * types.length };
}
export function preferredImage(images) {
    var _a, _b;
    let best = null;
    for (const image of images) {
        if (!image)
            continue;
        if (!best) {
            best = image;
            continue;
        }
        const area = (image.width || 0) * (image.height || 0);
        const bestArea = (best.width || 0) * (best.height || 0);
        if (area > bestArea)
            best = image;
        else if (area === bestArea && ((_a = image.imageId) !== null && _a !== void 0 ? _a : 0) < ((_b = best.imageId) !== null && _b !== void 0 ? _b : 0)) {
            best = image;
        }
    }
    return best;
}
export function resolveVodImage(input, type, language, languages) {
    var _a;
    const byLanguage = imagesByLanguage(input);
    const order = [];
    const push = (code) => {
        if (code && !order.includes(code))
            order.push(code);
    };
    push(canonicalAssetLanguage(language));
    for (const code of languages !== null && languages !== void 0 ? languages : [])
        push(canonicalAssetLanguage(code));
    push(IMAGE_FALLBACK_LANGUAGE);
    for (const code of [...byLanguage.keys()].sort())
        push(code);
    for (const code of order) {
        const hit = preferredImage(imagesOfType((_a = byLanguage.get(code)) !== null && _a !== void 0 ? _a : [], type));
        if (hit)
            return hit;
    }
    return null;
}
export const THUMBNAIL_TYPE_PREFERENCE = [
    "thumbnail",
    "cover",
    "banner",
    "poster",
    "other",
    "logo",
];
export const CHANNEL_LOGO_TYPE_PREFERENCE = [
    "logo",
    "thumbnail",
    "cover",
    "banner",
    "poster",
    "other",
];
export function resolveVodThumbnailImage(input) {
    for (const type of THUMBNAIL_TYPE_PREFERENCE) {
        const hit = resolveVodImage(input, type, input.language, input.languages);
        if (hit)
            return hit;
    }
    return null;
}
export function resolveChannelLogoImage(input) {
    for (const type of CHANNEL_LOGO_TYPE_PREFERENCE) {
        const hit = resolveVodImage(input, type, input.language, input.languages);
        if (hit)
            return hit;
    }
    return null;
}
export function resolveVodThumbnail(input, render = {}) {
    const image = resolveVodThumbnailImage(input);
    return image ? imageSrc(image, render) : null;
}
export const IMAGE_SCALER_PATH = "/api/media/imagescaler";
export const IMAGE_FITS = [
    "cover",
    "contain",
    "scale-down",
    "crop",
    "pad",
];
export const IMAGE_FORMATS = ["auto", "webp", "avif", "jpeg", "png"];
export const IMAGE_DEFAULT_FIT = "cover";
export const IMAGE_DEFAULT_FORMAT = "auto";
export const IMAGE_DEFAULT_QUALITY = 80;
export const IMAGE_MAX_DIMENSION = 4000;
export function imageStoragePath(url) {
    if (typeof url !== "string")
        return null;
    const trimmed = url.trim();
    if (trimmed === "")
        return null;
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed))
        return null;
    const path = trimmed.replace(/^\/+/, "");
    if (path === "" || path.includes(".."))
        return null;
    return path;
}
function renderQuery(options) {
    const params = [];
    const clampInt = (value, max) => {
        if (typeof value !== "number" || !Number.isFinite(value))
            return null;
        const n = Math.round(value);
        return n >= 1 && n <= max ? n : null;
    };
    const width = clampInt(options.width, IMAGE_MAX_DIMENSION);
    const height = clampInt(options.height, IMAGE_MAX_DIMENSION);
    const quality = clampInt(options.quality, 100);
    if (width)
        params.push(`w=${width}`);
    if (height)
        params.push(`h=${height}`);
    if (quality)
        params.push(`quality=${quality}`);
    if (options.fit && IMAGE_FITS.includes(options.fit)) {
        params.push(`fit=${options.fit}`);
    }
    if (options.format && IMAGE_FORMATS.includes(options.format)) {
        params.push(`format=${options.format}`);
    }
    return params.length ? `?${params.join("&")}` : "";
}
export function imageSrc(image, options = {}) {
    const path = imageStoragePath(image === null || image === void 0 ? void 0 : image.url);
    if (path === null) {
        const raw = typeof (image === null || image === void 0 ? void 0 : image.url) === "string" ? image.url.trim() : "";
        return /^https?:\/\//i.test(raw) ? raw : null;
    }
    return `${IMAGE_SCALER_PATH}/${path}${renderQuery(options)}`;
}
export function imageIssues(input) {
    var _a, _b;
    const rows = imageRows(input);
    const out = [];
    if (rows.length === 0)
        out.push({ kind: "no_images" });
    for (const row of rows) {
        const code = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language);
        const type = String((_a = row === null || row === void 0 ? void 0 : row.type) !== null && _a !== void 0 ? _a : "");
        if (!code) {
            out.push({
                kind: "unstorable_language",
                language: String((_b = row === null || row === void 0 ? void 0 : row.language) !== null && _b !== void 0 ? _b : ""),
            });
            continue;
        }
        if (!IMAGE_TYPES.includes(row === null || row === void 0 ? void 0 : row.type)) {
            out.push({ kind: "unknown_type", language: code, type });
        }
        const url = typeof (row === null || row === void 0 ? void 0 : row.url) === "string" ? row.url.trim() : "";
        if (url === "") {
            out.push({ kind: "empty_url", language: code, type });
        }
        else if (imageStoragePath(url) === null) {
            out.push({ kind: "absolute_url", language: code, type });
        }
        if (!((row === null || row === void 0 ? void 0 : row.width) > 0) || !((row === null || row === void 0 ? void 0 : row.height) > 0)) {
            out.push({ kind: "unmeasured_image", language: code, type });
        }
    }
    const textLanguages = localisedLanguages(input);
    const withImages = new Set(imageLanguages(input));
    const fallbackHasImages = withImages.has(IMAGE_FALLBACK_LANGUAGE);
    for (const language of textLanguages) {
        if (withImages.has(language) || fallbackHasImages)
            continue;
        out.push({ kind: "undelivered_language", language });
    }
    if (textLanguages.length > 0) {
        const offered = new Set(textLanguages);
        for (const [language, images] of imagesByLanguage(input)) {
            if (offered.has(language))
                continue;
            out.push({
                kind: "unreachable_images",
                language,
                count: images.length,
            });
        }
    }
    return out;
}
export function droppedImages(current, next) {
    const kept = new Set();
    for (const image of next) {
        const id = image === null || image === void 0 ? void 0 : image.imageId;
        if (typeof id === "number")
            kept.add(id);
    }
    return current.filter((image) => !kept.has(image.imageId));
}
export function readImages(raw, options = {}) {
    var _a;
    if (!Array.isArray(raw)) {
        if (raw && typeof raw === "object") {
            return {
                refusal: {
                    error: "collection_must_be_array",
                    field: "images",
                    hint: "`images` is an array of rows upstream, each carrying its own `language` and `type`, not an object keyed by language. Nothing on the image table is unique, so one language may legitimately carry several posters - a map would silently keep the last one.",
                },
            };
        }
        return { refusal: { error: "collection_must_be_array", field: "images" } };
    }
    const known = new Set(((_a = options.current) !== null && _a !== void 0 ? _a : [])
        .map((image) => image.imageId)
        .filter(Number.isFinite));
    const out = [];
    const seenIds = new Map();
    for (const [index, entry] of raw.entries()) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
            return { refusal: { error: "image_row_must_be_object", index } };
        }
        const row = entry;
        const language = canonicalAssetLanguage(row.language);
        if (!language) {
            return {
                refusal: {
                    error: "invalid_image_language",
                    index,
                    value: row.language,
                    hint: "`Image.language` is VarChar(3): a lowercase 2-3 letter code, no region suffix. It is what the delivery path filters on, so a wrong one is artwork no guest ever sees.",
                },
            };
        }
        const type = row.type === undefined ? DEFAULT_IMAGE_TYPE : row.type;
        if (!IMAGE_TYPES.includes(type)) {
            return {
                refusal: {
                    error: "invalid_image_type",
                    index,
                    value: row.type,
                    allowed: IMAGE_TYPES,
                    hint: "Six types, and the portal files a new image as `other` - which is a real answer, not a placeholder. Omitting the field means `other`.",
                },
            };
        }
        for (const field of ["url", "source"]) {
            const value = row[field];
            if (value !== undefined && value !== null && typeof value !== "string") {
                return {
                    refusal: { error: "image_field_must_be_text", field, index, value },
                };
            }
            const text = typeof value === "string" ? value : "";
            if (text.length > IMAGE_FIELD_LIMITS[field]) {
                return {
                    refusal: {
                        error: "field_too_long",
                        field,
                        index,
                        max: IMAGE_FIELD_LIMITS[field],
                        actual: text.length,
                        hint: `\`Image.${field}\` is VarChar(${IMAGE_FIELD_LIMITS[field]}) under STRICT_TRANS_TABLES, so an over-length value is an error three hops away that surfaces as a 500 naming nothing. Nothing here trims it for you.`,
                    },
                };
            }
        }
        const url = typeof row.url === "string" ? row.url.trim() : "";
        if (/^(data|blob):/i.test(url)) {
            return {
                refusal: {
                    error: "image_url_is_bytes",
                    index,
                    hint: "A `data:` or `blob:` url is the picture itself pretending to be a reference. The catalogue stores a path into the image container in 2 048 characters; upload the file and store where it went.",
                },
            };
        }
        for (const field of ["width", "height"]) {
            const value = row[field];
            if (value === undefined || value === null || value === "")
                continue;
            const n = typeof value === "number" ? value : Number(value);
            if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
                return {
                    refusal: {
                        error: "image_dimension_invalid",
                        index,
                        field,
                        value,
                        hint: "`Image.width`/`height` are non-negative integer pixel counts. 0 means nobody has measured this image yet, which is normal for an import; a fraction or a negative is not a measurement at all.",
                    },
                };
            }
        }
        let imageId;
        if (row.imageId !== undefined && row.imageId !== null) {
            if (!options.allowImageId) {
                return {
                    refusal: {
                        error: "image_id_not_on_create",
                        index,
                        hint: "`imageId` is minted by the catalogue's autoincrement, never by a caller. Send the row without one; the create response carries the id it was given.",
                    },
                };
            }
            const n = typeof row.imageId === "number" ? row.imageId : Number(row.imageId);
            if (!Number.isInteger(n) || n <= 0) {
                return {
                    refusal: { error: "invalid_image_id", index, value: row.imageId },
                };
            }
            const first = seenIds.get(n);
            if (first !== undefined) {
                return {
                    refusal: {
                        error: "duplicate_image_id",
                        imageId: n,
                        index,
                        firstIndex: first,
                        hint: "One row per image. Upstream both entries are applied in sequence to the same row, so the second silently wins and the picture the first one named is lost.",
                    },
                };
            }
            seenIds.set(n, index);
            imageId = n;
            if (options.current && !known.has(n)) {
                return {
                    refusal: {
                        error: "image_not_on_asset",
                        imageId: n,
                        index,
                        hint: "`updateImageList` upstream calls `image.update({where: {imageId}})` with NO ownership check, so an id belonging to another asset, a provider or a tag would have this asset's language, type and url written over it - and an id belonging to nothing answers a bare 500. Send the row without an `imageId` to add it.",
                    },
                };
            }
        }
        const value = {
            language,
            type: type,
            url,
            source: typeof row.source === "string" ? row.source.trim() : "",
            width: Number(row.width) || 0,
            height: Number(row.height) || 0,
        };
        out.push(imageId === undefined ? value : { ...value, imageId });
    }
    return { value: out };
}
